import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { classSelector, classesSelector, errorCodeSelector, optionsSelector } from './store/selector';
import {
  classClearAction,
  getClassFailureApiAction,
  getClassInitApiAction,
  getClassSuccessApiAction,
  getClassesFailureApiAction,
  getClassesInitApiAction,
  getClassesSuccessApiAction,
  postClassFailuireApiAction,
  postClassInitApiAction,
  postClassSeenFailuireApiAction,
  postClassSeenInitApiAction,
  postClassSuccessApiAction,
  putClassFailuireApiAction,
  putClassInitApiAction,
  putClassSuccessApiAction,
  resetOptionsAction,
  setOptionsAction
} from './store/reducer';
import { PostClassPayloadRequest } from './interfaces/post-class';
import { classApiService } from './services/api';
import { GetClassesParams } from './interfaces/get-classes';
import { PutClassPayloadRequest } from './interfaces/put-class';
import { toast } from 'react-toastify';

export function useClassCore() {
  const classes = useSelector(classesSelector);
  const clazz = useSelector(classSelector);
  const options = useSelector(optionsSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getClasses = useCallback(
    async (params: GetClassesParams, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(getClassesInitApiAction());
        const response = await classApiService.getClasses(params);
        dispatch(getClassesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(getClassesFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const getClass = useCallback(
    async (id: string, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(getClassInitApiAction());
        const response = await classApiService.getClass(id);
        dispatch(getClassSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(getClassFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const postClass = useCallback(
    async (
      body: PostClassPayloadRequest,
      onSuccess?: (id: string) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postClassInitApiAction());
        const response = await classApiService.postClass(body);
        dispatch(postClassSuccessApiAction());
        onSuccess?.(response.id);
      } catch (error: any) {
        console.error(error);
        if (error.request.status === 413) {
          toast.warn("El tamaño máximo de archivos permitidos es de 25MB!")
        }
        onFailure?.();
        dispatch(postClassFailuireApiAction());
      }
    },
    [dispatch]
  );

  const putClass = useCallback(
    async (
      id: string,
      body: PutClassPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putClassInitApiAction());
        await classApiService.putClass(id, body);
        dispatch(putClassSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        if (error.request.status === 413) {
          toast.warn("El tamaño máximo de archivos permitidos es de 25MB!")
        }
        onFailure?.();
        dispatch(putClassFailuireApiAction());
      }
    },
    [dispatch]
  );

  const postClassSeen = useCallback(
    async (
      classId: string,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postClassSeenInitApiAction());
        await classApiService.postClassSeen(classId);
        dispatch(postClassSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(postClassSeenFailuireApiAction());
      }
    },
    [dispatch]
  );

  const setOptions = useCallback((options: GetClassesParams) => {
    dispatch(setOptionsAction(options))
  }, [dispatch]);

  const resetOptions = useCallback(() => {
    dispatch(resetOptionsAction());
  }, [dispatch]);

  const clearClass = useCallback(() => {
    dispatch(classClearAction());
  }, [dispatch]);

  return {
    classes,
    clazz,
    options,
    errorCode,
    getClasses,
    getClass,
    postClass,
    postClassSeen,
    putClass,
    setOptions,
    resetOptions,
    clearClass,
  };
}
